import { useState, useEffect, useCallback } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useNavigate, useLocation } from "react-router-dom";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  const NAV_LINKS = [
    { label: "Servicios", href: "#servicios" },
    { label: "Identidad", href: "#identidad" },
    { label: "Diferencial", href: "#diferencial" },
    { label: "Estrategia", href: "#metodo" },
    { label: "Contacto", href: "#contacto" },
  ];

  const handleScroll = useCallback(() => setScrolled(window.scrollY > 20), []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const scrollTo = (href) => {
    setMobileOpen(false);
    if (!isHome) {
      navigate("/" + href);
      return;
    }
    if (href === "#hero") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !isHome
          ? "bg-white/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="header-logo"
            className="flex items-center gap-3 cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <img 
              src="/images/calle-logo-dark.png" 
              alt="Calle & Cía. Abogados" 
              className="h-16 w-auto"
            />
          </Link>

          {/* Center Nav */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                data-testid={`nav-${link.label.toLowerCase()}`}
                onClick={() => scrollTo(link.href)}
                className="text-sm text-calle-primary/60 hover:text-calle-primary transition-colors duration-200 font-medium font-body"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Right: CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <Button
              data-testid="header-cta-button"
              onClick={() => {
                // Future platform link - currently inactive
                alert("Plataforma en desarrollo. Próximamente disponible.");
              }}
              className="bg-calle-primary text-white hover:bg-calle-primary/90 rounded-sm px-7 py-5 text-sm font-medium font-body transition-all duration-300"
            >
              Acceso
            </Button>
          </div>

          {/* Mobile Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden text-calle-primary p-2"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-gray-200 pb-6">
          <nav className="flex flex-col px-6 pt-4 gap-4">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollTo(link.href)}
                className="text-left text-calle-primary/70 hover:text-calle-primary transition-colors py-2 font-medium font-body"
              >
                {link.label}
              </button>
            ))}
            <Button
              onClick={() => {
                setMobileOpen(false);
                alert("Plataforma en desarrollo. Próximamente disponible.");
              }}
              className="bg-calle-primary text-white hover:bg-calle-primary/90 rounded-sm px-6 py-5 font-medium font-body w-full mt-2"
            >
              Acceso
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
